import React, { useState, useMemo } from "react";
import {
  ShieldCheck, RotateCcw, LayoutTemplate,
  Trash2, RefreshCcw, PackageOpen,
  Tag, AlertTriangle, Megaphone, Sparkles,
  Calendar, Mail, Coins, Users,
} from "lucide-react";
import { createPortal } from "react-dom";
import { PageContainer } from "../ui/page-container";
import TrashHeader from "./TrashHeader";
import {
  ITrashedTemplate,
  ITrashCampaign,
  useTrashTemplates,
  useRestoreTemplate,
  usePermanentlyDeleteTemplate,
  useTrashCampaigns,
  useRestoreTrashedCampaign,
  useDeleteCampaign,
} from "core";
import { toast } from "components/ui/toast";
import { SkeletonSwitch, TrashListSkeleton } from "skeleton";
import {
  useDeleteTemplatePermanently,
  useDeleteTemplateFromTrash,
} from "core/features/content-builder/hooks";
import { EmptyState as SharedEmptyState } from "components/ui/empty-state";
import { ErrorState } from "components/ui/error-state";

/* ─── Helpers ─────────────────────────────────────────────── */

function daysRemaining(deletedAt?: string): number | null {
  if (!deletedAt) return null;
  const expiry = new Date(deletedAt).getTime() + 30 * 864e5;
  return Math.max(0, Math.ceil((expiry - Date.now()) / 864e5));
}

function fmtDate(raw?: string): string {
  if (!raw) return "—";
  const d = new Date(raw.replace(" ", "T"));
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

type ItemKind = "template" | "campaign" | "flow-template";
interface _FlowTrashedItem {
  id: number | string;
  name: string;
  tag?: string;
  description?: string;
  createdDate?: string;
  modifiedDate?: string;
  [key: string]: unknown;
}
interface UnifiedItem {
  id: string | number;
  name: string;
  kind: ItemKind;
  tag?: string;
  description?: string;
  thumbnail?: string;
  status?: string;
  templateName?: string;
  deletedAt?: string;
  sDate?: string;
  eDate?: string;
  emailUsed?: string;
  creditsUsed?: number;
  cGrp?: number[];
  raw: ITrashedTemplate | ITrashCampaign;
}

function toUnified(
  templates: ITrashedTemplate[],
  campaigns: ITrashCampaign[]
): UnifiedItem[] {
  const t: UnifiedItem[] = templates.map((t) => ({
    id: t.id,
    name: t.name || "Untitled Template",
    kind: "template",
    tag: t.tag,
    description: t.description,
    thumbnail: t.thumbnail || t.thumbnail_url,
    deletedAt: t.deletedAt,
    raw: t,
  }));
  const c: UnifiedItem[] = campaigns.map((c) => ({
    id: c.id,
    name: c.name || "Untitled Campaign",
    kind: "campaign",
    status: c.c_status,
    templateName: c.temp_name,
    deletedAt: c.deletedAt,
    sDate: c.s_date,
    eDate: c.e_date,
    emailUsed: c.email_used,
    creditsUsed: c.credits_used,
    cGrp: c.c_grp,
    raw: c,
  }));
  return [...t, ...c];
}

/* ─── Confirm Dialog ──────────────────────────────────────── */

interface ConfirmState {
  open: boolean;
  id: string | number | null;
  name: string;
  kind: ItemKind;
  action?: "delete-permanent" | "delete-from-trash";
}

const ConfirmDialog: React.FC<{
  state: ConfirmState;
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
}> = ({ state, onConfirm, onCancel, isPending }) => {
  if (!state.open) return null;

  const isRemove = state.action === "delete-from-trash";
  const title = isRemove ? "Remove from trash?" : "Delete permanently?";
  const body = isRemove
    ? "will be removed from trash only. The underlying file in storage will not be affected."
    : "will be permanently deleted from storage and cannot be recovered.";
  const confirmLabel = isRemove ? "Remove" : "Delete forever";
  const iconBg = isRemove ? "bg-gray-100 dark:bg-dark-700" : "bg-red-50 dark:bg-red-900/20";
  const iconColor = isRemove ? "text-content-muted dark:text-gray-400" : "text-red-500 dark:text-red-400";
  const btnCls = isRemove
    ? "bg-gray-700 hover:bg-gray-800 text-white"
    : "bg-red-500 hover:bg-red-600 text-white";

  return createPortal(
    <>
      <div className="fixed inset-0 z-[9990] bg-black/20 backdrop-blur-[2px]" onClick={onCancel} />
      <div className="fixed inset-0 z-[9991] flex items-center justify-center p-4 pointer-events-none">
        <div
          className="pointer-events-auto bg-surface dark:bg-dark-800 rounded-2xl shadow-2xl
            border border-line dark:border-dark-600 p-6 w-full max-w-[360px]"
          style={{ animation: "fadeIn 0.15s ease both" }}
        >
          <div className={`w-11 h-11 rounded-xl ${iconBg} flex items-center justify-center mx-auto mb-4`}>
            <Trash2 className={`w-5 h-5 ${iconColor}`} />
          </div>
          <h3 className="text-[15px] font-bold text-content dark:text-white text-center mb-1.5">
            {title}
          </h3>
          <p className="text-[12.5px] text-content-muted dark:text-gray-400 text-center mb-5 leading-relaxed">
            <span className="font-semibold text-gray-700 dark:text-gray-300">"{state.name}"</span>{" "}
            {body}
          </p>
          <div className="flex gap-2.5">
            <button
              onClick={onCancel}
              disabled={isPending}
              className="flex-1 h-9 rounded-xl text-[13px] font-semibold cursor-pointer
                bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300
                hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isPending}
              className={`flex-1 h-9 rounded-xl text-[13px] font-semibold cursor-pointer
                ${btnCls} transition-colors
                disabled:opacity-60 flex items-center justify-center gap-1.5`}
            >
              {isPending ? <RefreshCcw className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

/* ─── Thumbnail ──────────────────────────────────────────── */

const Thumb: React.FC<{ src?: string; name: string; kind: ItemKind }> = ({ src, name, kind }) => {
  const [err, setErr] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const imgSrc = (src && String(src).trim()) || "";
  const Icon = kind === "campaign" ? Megaphone : LayoutTemplate;
  return (
    <div
      className="w-[72px] h-[48px] rounded-xl overflow-hidden flex-shrink-0
        bg-gray-100 dark:bg-dark-700 border border-line dark:border-dark-600
        flex items-center justify-center shadow-sm"
    >
      {!err && imgSrc ? (
        <img
          src={imgSrc}
          alt={name}
          className={`w-full h-full object-cover object-top transition-opacity duration-200 ${loaded ? "opacity-100" : "opacity-0"}`}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          onError={() => setErr(true)}
        />
      ) : (
        <Icon className="w-5 h-5 text-gray-300 dark:text-gray-600" strokeWidth={1.5} />
      )}
    </div>
  );
};

/* ─── Type Badge ─────────────────────────────────────────── */

const TypeBadge: React.FC<{ kind: ItemKind }> = ({ kind }) => {
  if (kind === "template") return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide
      bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800/40 text-rose-600 dark:text-rose-400">
      <LayoutTemplate className="w-2.5 h-2.5" />Template
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide
      bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/40 text-blue-600 dark:text-blue-400">
      <Megaphone className="w-2.5 h-2.5" />Campaign
    </span>
  );
};

/* ─── Desktop Row ────────────────────────────────────────── */

const TrashRow: React.FC<{
  item: UnifiedItem;
  onRestore: () => void;
  onDelete: () => void;
  onDeleteFromTrash?: () => void;
  isRestoring: boolean;
}> = ({ item, onRestore, onDelete, onDeleteFromTrash: _onDeleteFromTrash, isRestoring }) => {
  const hoverColor = item.kind === "campaign"
    ? "hover:bg-blue-50/30 dark:hover:bg-blue-900/10 group-hover/row:text-blue-600 dark:group-hover/row:text-blue-400"
    : "hover:bg-rose-50/30 dark:hover:bg-rose-900/10 group-hover/row:text-rose-600 dark:group-hover/row:text-rose-400";

  return (
    <tr className={`group/row transition-colors ${hoverColor}`}>
      {/* Thumbnail */}
      <td className="pl-5 pr-3 py-3.5 w-px">
        <Thumb src={item.thumbnail} name={item.name} kind={item.kind} />
      </td>

      {/* Name + meta */}
      <td className="px-3 py-3.5">
        <div className="flex items-start gap-2 flex-wrap">
          <h3 className={`text-ui font-bold text-content dark:text-white leading-tight transition-colors truncate max-w-xs ${hoverColor}`}>
            {item.name}
          </h3>
          <TypeBadge kind={item.kind} />
        </div>
        {item.kind === "template" && item.tag && (
          <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full
            text-[10px] font-bold
            bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800/40
            text-rose-600 dark:text-rose-400">
            <Tag className="w-2.5 h-2.5 shrink-0" />{item.tag}
          </span>
        )}
        {item.kind === "template" && item.description && (
          <p className="text-micro text-content-subtle dark:text-gray-500 mt-1 line-clamp-1 max-w-sm">
            {item.description}
          </p>
        )}
        {item.kind === "campaign" && (
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5">
            {item.status && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-[4px] text-[10px] font-bold uppercase tracking-wider
                bg-gray-100 dark:bg-dark-700 text-content-muted dark:text-gray-400">
                {item.status}
              </span>
            )}
            {item.templateName && (
              <p className="text-[11px] text-content-muted dark:text-gray-400 flex items-center gap-1.5">
                <LayoutTemplate className="w-3.5 h-3.5" />
                <span className="truncate max-w-[120px]">{item.templateName}</span>
              </p>
            )}
            {item.sDate && (
              <p className="text-[11px] text-content-muted dark:text-gray-400 flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                <span>{fmtDate(item.sDate)} {item.eDate ? `- ${fmtDate(item.eDate)}` : ''}</span>
              </p>
            )}
            {item.emailUsed && (
              <p className="text-[11px] text-content-muted dark:text-gray-400 flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" />
                <span className="truncate max-w-[120px]">{item.emailUsed.split('<')[0].trim()}</span>
              </p>
            )}
            {item.creditsUsed !== undefined && item.creditsUsed !== null && (
              <p className="text-[11px] text-content-muted dark:text-gray-400 flex items-center gap-1.5">
                <Coins className="w-3.5 h-3.5" />
                <span>{item.creditsUsed}</span>
              </p>
            )}
            {item.cGrp && item.cGrp.length > 0 && (
              <p className="text-[11px] text-content-muted dark:text-gray-400 flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" />
                <span>{item.cGrp.length}</span>
              </p>
            )}
          </div>
        )}
      </td>

      {/* Actions */}
      <td className="pr-5 pl-3 py-3.5 w-px" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <button
            onClick={onRestore}
            disabled={isRestoring}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl
              text-meta font-semibold cursor-pointer border whitespace-nowrap
              bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700
              text-blue-600 dark:text-blue-400
              hover:bg-blue-100 dark:hover:bg-blue-900/40
              transition-colors disabled:opacity-50"
          >
            {isRestoring ? <RefreshCcw className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
            Restore
          </button>
          <button
            onClick={onDelete}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl
              text-meta font-semibold cursor-pointer border whitespace-nowrap
              bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700
              text-red-600 dark:text-red-400
              hover:bg-red-100 dark:hover:bg-red-900/40
              transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete
          </button>
        </div>
      </td>
    </tr>
  );
};

const TrashCard: React.FC<{
  item: UnifiedItem;
  onRestore: () => void;
  onDelete: () => void;
  onDeleteFromTrash?: () => void;
  isRestoring: boolean;
}> = ({ item, onRestore, onDelete, onDeleteFromTrash: _onDeleteFromTrash, isRestoring }) => {
  const accentBar = item.kind === "campaign"
    ? "from-blue-400 via-indigo-500 to-blue-400"
    : "from-rose-400 via-pink-500 to-rose-400";

  return (
    <div className="bg-surface dark:bg-dark-800 rounded-2xl border border-line dark:border-dark-700 overflow-hidden shadow-sm">
      <div className={`h-[2px] bg-gradient-to-r ${accentBar}`} />
      <div className="p-4 flex flex-col gap-3.5">
        <div className="flex gap-3 items-start">
          <Thumb src={item.thumbnail} name={item.name} kind={item.kind} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-ui-lg font-bold text-content dark:text-white truncate leading-tight">
                {item.name}
              </h3>
              <TypeBadge kind={item.kind} />
            </div>
            {item.kind === "template" && item.tag && (
              <span className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full
                text-[11px] font-semibold
                bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800/40
                text-rose-600 dark:text-rose-400">
                <Tag className="w-2.5 h-2.5 shrink-0" />{item.tag}
              </span>
            )}
            {item.kind === "campaign" && (
              <div className="mt-1 flex flex-col gap-1.5 border-t border-line-subtle dark:border-dark-700 pt-2">
                {item.status && (
                  <div className="flex items-center">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-[4px] text-[10px] font-bold uppercase tracking-wider
                      bg-gray-100 dark:bg-dark-700 text-content-muted dark:text-gray-400">
                      {item.status}
                    </span>
                  </div>
                )}
                {item.templateName && (
                  <p className="text-[11px] text-content-muted dark:text-gray-400 flex items-center gap-1.5">
                    <LayoutTemplate className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{item.templateName}</span>
                  </p>
                )}
                {item.sDate && (
                  <p className="text-[11px] text-content-muted dark:text-gray-400 flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{fmtDate(item.sDate)} {item.eDate ? `- ${fmtDate(item.eDate)}` : ''}</span>
                  </p>
                )}
                {item.emailUsed && (
                  <p className="text-[11px] text-content-muted dark:text-gray-400 flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{item.emailUsed.split('<')[0].trim()}</span>
                  </p>
                )}
                {item.creditsUsed !== undefined && item.creditsUsed !== null && (
                  <p className="text-[11px] text-content-muted dark:text-gray-400 flex items-center gap-1.5">
                    <Coins className="w-3.5 h-3.5 shrink-0" />
                    <span>{item.creditsUsed} Credits used</span>
                  </p>
                )}
                {item.cGrp && item.cGrp.length > 0 && (
                  <p className="text-[11px] text-content-muted dark:text-gray-400 flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5 shrink-0" />
                    <span>{item.cGrp.length} Contact Group{item.cGrp.length > 1 ? 's' : ''}</span>
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
        {item.kind === "template" && item.description && (
          <p className="text-meta text-content-muted dark:text-gray-400 leading-relaxed line-clamp-2">
            {item.description}
          </p>
        )}
        <div className="flex items-center gap-2">
          <button
            onClick={onRestore}
            disabled={isRestoring}
            className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-xl
              text-[13px] font-semibold cursor-pointer border
              bg-blue-50 dark:bg-blue-900/15 border-blue-200 dark:border-blue-800/40
              text-blue-600 dark:text-blue-400
              hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors disabled:opacity-50"
          >
            {isRestoring ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
            Restore
          </button>
          <button
            onClick={onDelete}
            disabled={isRestoring}
            className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-xl
              text-[13px] font-semibold cursor-pointer border
              bg-red-50 dark:bg-red-900/15 border-red-200 dark:border-red-800/40
              text-red-600 dark:text-red-400
              hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Empty State ────────────────────────────────────────── */

/*
 * Trash's empty state was hand-built — its own icon halo, its own type sizes
 * and its own row of coloured pills. The shared EmptyState already expresses
 * exactly this shape (icon, title, description, hints), so the content is kept
 * verbatim and only the shell is now shared.
 */
const TrashEmptyState: React.FC<{ filtered?: boolean }> = ({ filtered }) =>
  filtered ? (
    <SharedEmptyState
      bare
      variant="no-results"
      icon={PackageOpen}
      title="No items in this category"
      description="Switch to a different filter to see other deleted items."
    />
  ) : (
    <SharedEmptyState
      bare
      icon={PackageOpen}
      tone="danger"
      title="Your trash is empty"
      description="Deleted templates and campaigns appear here, and are permanently removed after 30 days."
      hints={[
        { icon: ShieldCheck, title: "Protected 30 days", description: "Nothing is removed for good before then." },
        { icon: RotateCcw, title: "Restore anytime", description: "Put an item back where it came from." },
        { icon: Sparkles, title: "Auto-clean on", description: "Older items are cleared for you." },
      ]}
    />
  );

/* ─── Segment Filter ─────────────────────────────────────── */

type Filter = "template" | "campaign" | "flow-template";

const SegmentedFilter: React.FC<{
  active: Filter;
  onChange: (f: Filter) => void;
  templateCount: number;
  campaignCount: number;
}> = ({ active, onChange, templateCount, campaignCount }) => {
  const tabs: { key: Filter; label: string; count: number; icon: React.FC<any> }[] = [
    { key: "template", label: "Templates", count: templateCount, icon: LayoutTemplate },
    { key: "campaign", label: "Campaigns", count: campaignCount, icon: Megaphone },
  ];

  return (
    <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-gray-100 dark:bg-dark-700 border border-line dark:border-dark-600">
      {tabs.map(({ key, label, count, icon: Icon }) => (
        <button
          key={key}
          onClick={() => onChange(key)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-meta font-semibold transition-all duration-150 whitespace-nowrap
            ${active === key
              ? "bg-surface dark:bg-dark-800 text-content dark:text-white shadow-sm border border-line dark:border-dark-600"
              : "text-content-muted dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            }`}
        >
          <Icon className="w-3.5 h-3.5" />
          {label}
          <span className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold
            ${active === key ? "bg-gray-100 dark:bg-dark-700 text-content-secondary dark:text-gray-300" : "bg-gray-200 dark:bg-dark-600 text-content-muted dark:text-gray-400"}`}>
            {count}
          </span>
        </button>
      ))}
    </div>
  );
};

/* ─── Main Component ─────────────────────────────────────── */

export default function Trash() {
  // ── Data ──────────────────────────────────────────────────
  const {
    data: trashedTemplates,
    isLoading: loadingTemplates,
    isError: templatesError,
    error: templatesErrorObj,
    refetch: refetchTemplates,
  } = useTrashTemplates();
  const {
    data: trashedCampaigns,
    isLoading: loadingCampaigns,
    isError: campaignsError,
    error: campaignsErrorObj,
    refetch: refetchCampaigns,
  } = useTrashCampaigns();

  // Split trashedTemplates by tag — no extra network call needed.
  // tag === "drawflow" → Flow Templates; everything else → Templates.
  const isLoading = loadingTemplates || loadingCampaigns;
  /*
   * A failed request used to render "Your trash is empty", telling the user
   * their deleted items were gone. Emptiness and failure are different answers.
   */
  const hasError = templatesError || campaignsError;
  const retryAll = () => {
    if (templatesError) refetchTemplates();
    if (campaignsError) refetchCampaigns();
  };

  const restoreTemplateMutation = useRestoreTemplate();
  const deleteTemplateMutation = usePermanentlyDeleteTemplate();
  const restoreCampaignMutation = useRestoreTrashedCampaign();
  const deleteCampaignMutation = useDeleteCampaign();
  const deletePermanentMutation = useDeleteTemplatePermanently();
  const deleteFromTrashMutation = useDeleteTemplateFromTrash();

  const [filter, setFilter] = useState<Filter>("template");
  const [confirmDialog, setConfirmDialog] = useState<ConfirmState>({
    open: false, id: null, name: "", kind: "template",
  });
  const [restoringId, setRestoringId] = useState<string | number | null>(null);

  const allItems = useMemo(
    () => toUnified(trashedTemplates ?? [], trashedCampaigns ?? []),
    [trashedTemplates, trashedCampaigns]
  );

  const filteredItems = useMemo(() => {
    return allItems.filter((i) => i.kind === filter);
  }, [allItems, filter]);

  const templateCount = trashedTemplates?.length ?? 0;
  const campaignCount = trashedCampaigns?.length ?? 0;
  const totalCount = templateCount + campaignCount;
  const hasItems = totalCount > 0;

  const urgentCount = useMemo(() =>
    filteredItems.filter((i) => {
      const d = daysRemaining(i.deletedAt);
      return d !== null && d <= 5;
    }).length,
    [filteredItems]
  );

  // ── Handlers ──────────────────────────────────────────────
  const handleRestore = async (item: UnifiedItem) => {
    setRestoringId(item.id);
    try {
      if (item.kind === "template") {
        await restoreTemplateMutation.mutateAsync(item.id);
      } else {
        await restoreCampaignMutation.mutateAsync(item.id);
      }
      toast.success(`"${item.name}" restored successfully`);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to restore");
    } finally {
      setRestoringId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDialog.id) return;
    try {
      if (confirmDialog.kind === "template") {
        if (confirmDialog.action === "delete-from-trash") {
          await deleteFromTrashMutation.mutateAsync(confirmDialog.id);
        } else {
          await deleteTemplateMutation.mutateAsync(confirmDialog.id);
        }
      } else {
        await deleteCampaignMutation.mutateAsync(confirmDialog.id);
      }
      toast.success(`"${confirmDialog.name}" permanently deleted`);
      setConfirmDialog({ open: false, id: null, name: "", kind: "template" });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to delete");
    }
  };

  const isPendingDelete = deleteTemplateMutation.isPending || deleteCampaignMutation.isPending
    || deletePermanentMutation.isPending || deleteFromTrashMutation.isPending;

  // ── Render ────────────────────────────────────────────────
  return (
    <PageContainer>
      <TrashHeader hasItems={hasItems} itemCount={totalCount} />

      <div className="flex flex-col gap-5">
        <SkeletonSwitch
          isLoading={isLoading}
          skeleton={<TrashListSkeleton count={4} />}
        >
          {hasItems ? (
            <>
              {/* ── Filter bar ── */}
              <div className="flex items-center justify-between flex-wrap gap-3">
                <SegmentedFilter
                  active={filter}
                  onChange={setFilter}
                  templateCount={templateCount}
                  campaignCount={campaignCount}
                />
                {urgentCount > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold
                    bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40
                    text-amber-600 dark:text-amber-400">
                    <AlertTriangle className="w-3 h-3" />{urgentCount} expiring soon
                  </span>
                )}
              </div>

              {/* ── Mobile cards ── */}
              <div className="sm:hidden flex flex-col gap-3">
                {filteredItems.length === 0 ? (
                  <TrashEmptyState filtered />
                ) : (
                  filteredItems.map((item) => (

                    <TrashCard
                      key={`${item.kind}-${item.id}`}
                      item={item}
                      onRestore={() => handleRestore(item)}
                      onDelete={() => setConfirmDialog({ open: true, id: item.id, name: item.name, kind: item.kind })}
                      onDeleteFromTrash={() => setConfirmDialog({ open: true, id: item.id, name: item.name, kind: item.kind, action: "delete-from-trash" })}
                      isRestoring={restoringId === item.id}
                    />
                  ))
                )}
              </div>

              {/* ── Desktop table card ── */}
              <div className="hidden sm:block rounded-2xl overflow-hidden
                border border-line dark:border-dark-700
                bg-surface dark:bg-dark-800 shadow-sm">

                {/* Accent bar */}
                <div className="h-[2px] bg-gradient-to-r from-rose-400 via-pink-500 to-indigo-400" />

                {/* Card header */}
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-line-subtle dark:border-dark-700">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-rose-50 dark:bg-rose-900/20
                      border border-rose-200 dark:border-rose-800/40
                      flex items-center justify-center shrink-0">
                      <Trash2 className="w-3.5 h-3.5 text-rose-500 dark:text-rose-400" />
                    </div>
                    <div>
                      <h2 className="text-ui-lg font-bold text-content dark:text-white leading-tight">
                        {filter === "template" ? "Deleted Templates" : "Deleted Campaigns"}
                      </h2>
                      <p className="text-[11px] text-content-muted dark:text-gray-400">
                        {filteredItems.length} item{filteredItems.length !== 1 ? "s" : ""}
                      </p>
                    </div>
                  </div>
                  {/* Right: stat chips */}
                  <div className="flex items-center gap-2">
                    {urgentCount > 0 && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold
                        bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40
                        text-amber-600 dark:text-amber-400">
                        <AlertTriangle className="w-3 h-3" />{urgentCount} expiring soon
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold
                      bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/40
                      text-emerald-600 dark:text-emerald-400">
                      <ShieldCheck className="w-3 h-3" />{filteredItems.length - urgentCount} safe
                    </span>
                  </div>
                </div>

                {filteredItems.length === 0 ? (
                  <TrashEmptyState filtered />
                ) : (
                  /* The card clips with `overflow-hidden` so its rounded
                     corners hold; the table needs its own scroll region or a
                     wide row is cut off on a narrow tablet rather than
                     scrolled to. Same pattern as the shared DataTable. */
                  <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50/60 dark:bg-dark-700/30 border-b border-line-subtle dark:border-dark-700">
                        <th className="pl-5 pr-3 py-2.5 w-px" />
                        <th className="px-3 py-2.5 text-left text-[10px] font-extrabold uppercase
                          tracking-wider text-content-subtle dark:text-gray-500">Item</th>
                        <th className="pr-5 pl-3 py-2.5 text-right text-2xs font-extrabold uppercase
                          tracking-wider text-content-subtle dark:text-gray-500 w-px whitespace-nowrap">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
                      {filteredItems.map((item) => (

                        <TrashRow
                          key={`${item.kind}-${item.id}`}
                          item={item}
                          onRestore={() => handleRestore(item)}
                          onDelete={() => setConfirmDialog({ open: true, id: item.id, name: item.name, kind: item.kind })}
                          onDeleteFromTrash={() => setConfirmDialog({ open: true, id: item.id, name: item.name, kind: item.kind, action: "delete-from-trash" })}
                          isRestoring={restoringId === item.id}
                        />
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>
            </>
          ) : hasError ? (
            /* Failure is a different answer from "nothing here" — say so, and
               offer the retry rather than implying the trash is empty. */
            <ErrorState
              title="Couldn't load your trash"
              description="The request didn't complete. Nothing has been deleted — this is only the view."
              error={templatesErrorObj ?? campaignsErrorObj}
              onRetry={retryAll}
            />
          ) : (
            /* Global empty state */
            <div className="bg-surface dark:bg-dark-800 rounded-2xl border border-line dark:border-dark-700 shadow-sm overflow-hidden">
              <div className="h-[2px] bg-gradient-to-r from-rose-400 via-pink-500 to-indigo-400" />
              <TrashEmptyState />
              <div className="flex items-center justify-between px-5 py-3
                border-t border-line-subtle dark:border-dark-700 bg-gray-50/60 dark:bg-dark-700/30">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400 flex-shrink-0" />
                  <span className="text-[12px] text-content-muted dark:text-gray-400">
                    Items older than 30 days are{" "}
                    <span className="font-semibold text-gray-700 dark:text-gray-300">permanently deleted</span>
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                  </span>
                  <span className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400">All clear</span>
                </div>
              </div>
            </div>
          )}
        </SkeletonSwitch>
      </div>

      <ConfirmDialog
        state={confirmDialog}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDialog({ open: false, id: null, name: "", kind: "template" })}
        isPending={isPendingDelete}
      />
    </PageContainer>
  );
}
